import api from '@/api';
import { getPropertyArray } from '@/util/formatters';
import apiV2 from '@/api/v2';
import { DEFAULT_LIMIT } from '@/constants/pagination';
import { mapPaginationToV1Format } from '@/util/query';

export default {
  namespaced: true,
  state: {
    currentOffer: null,
    objectsInCurrentOffer: [],
    activeObjectId: null,
    objectsOfferId: null,
    placesToSelect: [],
    placesToRemove: [],
    objectsToSelect: [],
    objectsToRemove: [],
    objectsAddedInOffer: [],
    forceCheckboxUpdate: 0,
    offerList: [],
  },
  mutations: {
    SET_CURRENT_OFFER(state, { data }) {
      state.currentOffer = data;
    },
    SET_OBJECTS_IN_OFFER(state, nodes) {
      state.objectsInCurrentOffer = [...state.objectsInCurrentOffer, ...nodes];
    },
    SET_ACTIVE_OBJECT(state, id) {
      state.activeObjectId = id;
    },
    SET_OBJECTS_OFFER_ID(state, id) {
      state.objectsOfferId = id;
    },
    CLEAR_OBJECTS_IN_OFFER(state) {
      state.objectsInCurrentOffer = [];
    },
    SET_OFFER_LIST(state, offers) {
      state.offerList = offers;
    },
  },
  getters: {
    activeObjectIndex: (state) => {
      return state.objectsInCurrentOffer.findIndex(
        ({ id }) => id === state.activeObjectId,
      );
    },
    lastObjectIndex: (state) => {
      return state.objectsInCurrentOffer.length - 1;
    },
    nextObjectId: (state, getters) => {
      const nextIndex = getters.activeObjectIndex + 1;

      if (nextIndex > getters.lastObjectIndex) return null;

      return state.objectsInCurrentOffer[nextIndex].id;
    },
    prevObjectId: (state, getters) => {
      const prevIndex = getters.activeObjectIndex - 1;

      if (prevIndex < 0) return null;

      return state.objectsInCurrentOffer[prevIndex].id;
    },
    activeOfferId: (state) => state.objectsOfferId,
    activeObject: (state, getters) => {
      return state.objectsInCurrentOffer[getters.activeObjectIndex];
    },
    selectedPlaceIds: (state) => getPropertyArray(state.placesToSelect, 'id'),
    removedPlaceIds: (state) => getPropertyArray(state.placesToRemove, 'id'),
    selectedObjectIds: (state) => getPropertyArray(state.objectsToSelect, 'id'),
    removedObjectIds: (state) => getPropertyArray(state.objectsToRemove, 'id'),
    offerObjectIds: (state) =>
      getPropertyArray(state.objectsAddedInOffer, 'id'),
    currentOfferIndex: (state) =>
      state.offerList.indexOf(state.currentOffer.id),
    nextOffer: (state, getters) => {
      if (
        !state.currentOffer ||
        [-1, state.offerList.length - 1].includes(getters.currentOfferIndex)
      )
        return null;

      return state.offerList[getters.currentOfferIndex + 1];
    },
    prevOffer: (state, getters) => {
      if (!state.currentOffer || [-1, 0].includes(getters.currentOfferIndex))
        return null;

      return state.offerList[getters.currentOfferIndex - 1];
    },
  },
  actions: {
    async fetchOffer({ commit }, offerId) {
      const offer = await apiV2.get(`offers/${offerId}`);
      commit('SET_CURRENT_OFFER', offer);

      return offer;
    },
    editOffer(ctx, { id, data }) {
      return api.patch(`offers/${id}`, data);
    },
    addOffer(ctx, data) {
      return api.post(`offers`, data);
    },
    async fetchAllOfferObjects({ commit, dispatch }, { offerId, page = 1 }) {
      try {
        commit('SET_OBJECTS_OFFER_ID', offerId);

        const { data } = await apiV2.get('objects', {
          params: {
            offerId,
            page,
            perPage: DEFAULT_LIMIT,
          },
        });

        const pagination = mapPaginationToV1Format(
          DEFAULT_LIMIT,
          page,
          data.totalCount,
        );

        commit('SET_OBJECTS_IN_OFFER', data.nodes);

        const { to, total } = pagination;

        if (total > to) {
          await dispatch('fetchAllOfferObjects', {
            offerId,
            page: page + 1,
          });
        }
      } catch {
        commit('CLEAR_OBJECTS_IN_OFFER');
      }
    },
    setActiveObject({ commit }, id) {
      commit('SET_ACTIVE_OBJECT', id);
    },
    clearState({ commit }) {
      commit('CLEAR_OBJECTS_IN_OFFER');
      commit('SET_ACTIVE_OBJECT', null);
      commit('SET_OBJECTS_OFFER_ID', null);
      commit('SET_CURRENT_OFFER', { data: null });
    },
    fetchOffers(ctx, params) {
      return apiV2.get('offers', { params });
    },
    fetchObjectRelations(ctx, { params, offerId }) {
      return apiV2.get(`/objects`, { params: { ...params, offerId } });
    },
    fetchSingleObjectRelations(ctx, { offerId, objectId }) {
      return api.get(`/offers/${offerId}/object-relations/${objectId}`);
    },
    async acceptOffer(ctx, { relationId, type }) {
      await api.post(
        `/offers/object-relations/${relationId}/${type}/accept`,
        {},
      );
    },
    async rejectOffer(ctx, { relationId, type }) {
      await api.post(
        `/offers/object-relations/${relationId}/${type}/reject`,
        {},
      );
    },
    setOfferList({ commit }, offers) {
      commit('SET_OFFER_LIST', getPropertyArray(offers, 'id'));
    },
    clearOfferObjectsList({ commit }) {
      commit('CLEAR_OBJECTS_IN_OFFER');
    },
  },
};
